import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ChevronLeft,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingCart,
  Truck,
  Wrench,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import OptionGrid from "@/components/OptionGrid";
import { Reveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { fetchProduct, fetchProducts, formatNZD } from "@/lib/api";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selections, setSelections] = useState({});
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setActiveImg(0);
    setQty(1);
    fetchProduct(slug)
      .then((p) => {
        setProduct(p);
        const defaults = {};
        p.options.forEach((g) => (defaults[g.id] = g.choices[0].id));
        setSelections(defaults);
      })
      .catch(() => setProduct(false));
    fetchProducts()
      .then((all) => setRelated(all.filter((p) => p.slug !== slug)))
      .catch(() => setRelated([]));
  }, [slug]);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    let total = product.base_price;
    product.options.forEach((g) => {
      const choice =
        g.choices.find((c) => c.id === selections[g.id]) || g.choices[0];
      total += choice.price_delta;
    });
    return total;
  }, [product, selections]);

  if (product === null)
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-zinc-500" data-testid="product-loading">
        Loading kit…
      </div>
    );
  if (product === false)
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-3xl font-extrabold text-ink">Kit not found</h1>
        <Link to="/shop" data-testid="product-notfound-back" className="rounded-[3px] bg-volt px-6 py-3 font-bold text-white">
          Back to Shop
        </Link>
      </div>
    );

  const shippingInfo = product.shipping || { flat_rate: 0, note: "", points: [] };

  const handleAdd = () => {
    addItem({
      type: "product",
      productId: product.id,
      badge: product.badge,
      name: product.name,
      image: product.images[0],
      unitPrice,
      qty,
      options: { ...selections },
      detailLines: product.options.map((g) => {
        const c = g.choices.find((x) => x.id === selections[g.id]) || g.choices[0];
        return `${g.label}: ${c.name}`;
      }),
    });
    toast.success(`${product.name} added to cart`, {
      description: `${formatNZD(unitPrice)} × ${qty} — configured and ready.`,
    });
  };

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
      <div className="flex items-center gap-2 py-6 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
        <Link to="/shop" data-testid="product-breadcrumb-shop" className="hover:text-volt transition-colors duration-200">
          Shop
        </Link>
        <ChevronLeft className="h-3.5 w-3.5 rotate-180" />
        <span className="text-ink">{product.name}</span>
      </div>

      {/* ---------- CONFIGURATOR SPLIT ---------- */}
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="lg:sticky lg:top-24 self-start">
          <div className="overflow-hidden rounded-2xl border border-black/10 bg-surface">
            <motion.img
              key={activeImg}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              src={product.images[activeImg]}
              alt={`${product.name} view ${activeImg + 1}`}
              className="aspect-[4/3] w-full object-cover"
              data-testid="product-gallery-main"
            />
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {product.images.map((img, i) => (
              <button
                key={i}
                type="button"
                data-testid={`product-gallery-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                aria-label={`View image ${i + 1}`}
                className={`overflow-hidden rounded-lg border bg-surface transition-colors duration-200 ${
                  activeImg === i ? "border-volt" : "border-black/10 hover:border-black/30"
                }`}
              >
                <img src={img} alt="" className="aspect-square w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="inline-block rounded-full border border-volt/30 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-volt">
            {product.badge}
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-5xl" data-testid="product-name">
            {product.name}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-zinc-600">{product.tagline}</p>

          <motion.p
            key={unitPrice}
            initial={{ opacity: 0.4, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-6 font-mono text-4xl font-bold text-volt"
            data-testid="product-price"
          >
            {formatNZD(unitPrice)}
          </motion.p>

          <div className="mt-8 space-y-7">
            {product.options.map((g) => (
              <OptionGrid
                key={g.id}
                group={g}
                value={selections[g.id]}
                onChange={(gid, cid) => setSelections((s) => ({ ...s, [gid]: cid }))}
              />
            ))}
          </div>

          <div className="mt-9 flex flex-wrap items-stretch gap-4">
            <div className="flex items-center rounded-[3px] border border-black/15" data-testid="product-qty">
              <button
                type="button"
                data-testid="product-qty-dec"
                aria-label="Decrease quantity"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="flex h-full w-11 items-center justify-center text-zinc-500 transition-colors duration-200 hover:text-volt"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-10 text-center font-mono text-lg font-bold text-ink" data-testid="product-qty-value">
                {qty}
              </span>
              <button
                type="button"
                data-testid="product-qty-inc"
                aria-label="Increase quantity"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                className="flex h-full w-11 items-center justify-center text-zinc-500 transition-colors duration-200 hover:text-volt"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              onClick={handleAdd}
              data-testid="product-add-to-cart"
              className="flex flex-1 items-center justify-center gap-2 rounded-[3px] bg-volt px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-200 hover:bg-volt-dim"
            >
              <ShoppingCart className="h-4 w-4" strokeWidth={2.5} />
              Add to Cart — {formatNZD(unitPrice * qty)}
            </button>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <p className="flex items-center gap-2 text-xs text-zinc-600">
              <Truck className="h-4 w-4 text-volt" />
              {product.badge === "Combo Kit"
                ? "$170 flat shipping, NZ-wide"
                : "$10 flat shipping alone — free with a kit"}
            </p>
            {product.badge === "Combo Kit" && (
              <p className="flex items-center gap-2 text-xs text-zinc-600">
                <Wrench className="h-4 w-4 text-volt" /> Ship to door, pickup, or installation
              </p>
            )}
            <p className="flex items-center gap-2 text-xs text-zinc-600">
              <ShieldCheck className="h-4 w-4 text-volt" /> Manual invoice — no card now
            </p>
          </div>
        </div>
      </div>

      {/* ---------- SECTIONS ---------- */}
      <div className="mt-24 space-y-16">
        <Section title="Description" testid="product-section-description">
          <div className="max-w-3xl space-y-4">
            {product.description.map((para, i) => (
              <p key={i} className="text-base leading-relaxed text-zinc-600">
                {para}
              </p>
            ))}
          </div>
        </Section>

        <Section title="Specifications" testid="product-section-specs">
          <div className="max-w-3xl overflow-hidden rounded-xl border border-black/10">
            {product.specs.map((s, i) => (
              <div
                key={s.label}
                className={`grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 px-5 py-4 ${i % 2 === 0 ? "bg-surface" : "bg-white"}`}
              >
                <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500">
                  {s.label}
                </span>
                <span className="text-sm text-zinc-700 sm:col-span-2">{s.value}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="What's Included" testid="product-section-included">
          <ul className="grid max-w-3xl gap-3 sm:grid-cols-2">
            {product.included.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm text-zinc-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Compatibility" testid="product-section-compatibility">
          <ul className="max-w-3xl space-y-2.5">
            {product.compatibility.map((c) => (
              <li key={c} className="flex items-start gap-3 text-sm leading-relaxed text-zinc-600">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-volt" />
                {c}
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Shipping" testid="product-section-shipping">
          <div className="max-w-3xl rounded-xl border border-black/10 bg-surface p-6">
            <p className="font-mono text-sm text-volt">
              {formatNZD(shippingInfo.flat_rate)} — flat rate, NZ-wide
            </p>
            <p className="mt-2 text-xs text-zinc-500">{shippingInfo.note}</p>
            <ul className="mt-4 space-y-2">
              {shippingInfo.points.map((pt) => (
                <li key={pt} className="flex items-start gap-3 text-sm text-zinc-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-volt" strokeWidth={3} />
                  {pt}
                </li>
              ))}
            </ul>
          </div>
        </Section>

        <Section title="FAQ" testid="product-section-faq">
          <Accordion type="single" collapsible className="max-w-3xl">
            {product.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger data-testid={`product-faq-trigger-${i}`} className="text-left text-base text-ink hover:text-volt hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-zinc-600">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Section>

        {related.length > 0 && (
          <Section title="Related" testid="product-section-related">
            <div className="grid gap-6 md:grid-cols-2">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} delay={i * 0.1} />
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <Reveal>
      <section data-testid={testid}>
        <h2 className="mb-6 font-display text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
          {title}
        </h2>
        {children}
      </section>
    </Reveal>
  );
}
